import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SearchBarComponent } from '@app/ui/search-bar/search-bar.component';
import { PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { CustomCodeService } from '@app/shared/services/custom-codes/custom-code.service';
import { PropertyOnboardingService } from '@app/shared/services/property-onboarding/property-onboarding.service';
import { RuleService } from '@app/shared/services/rule/rule.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { TeamService } from '@app/shared/services/team/team.service';

@Component({
  standalone: false,
  selector: 'sbnb-property-detail-messaging-rules',
  templateUrl: './property-detail-messaging-rules.component.html',
  styleUrls: ['./property-detail-messaging-rules.component.scss'],
})
export class PropertyDetailMessagingRulesComponent implements OnInit, OnChanges {
  @Input() property: any;
  @Input() type: string;

  @Output() onboardingChange: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('searchComponent')
  searchComponent: SearchBarComponent;

  @Input() onboardingMode: boolean;

  types = [
    {
      id: 'messaging-rules',
      url: '/gx/rule/',
      filterId: 'messaging-rules',
      label: 'Messaging rules',
      columns: [
        {
          label: 'Rule name',
          flex: '1 1 100%',
        },
        {
          label: 'Sending time',
          flex: '30',
        },
        {
          label: 'Hosts',
          flex: '120px',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
        {
          label: 'Platforms',
          flex: '80px',
        },
      ],
    },
    {
      id: 'review-rules',
      url: '/gx/reviews/review/',
      filterId: 'reviews',
      label: 'Review rules',
      columns: [
        {
          label: 'Review rule name',
          flex: '1 1 100%',
        },
        {
          label: 'Sending time',
          flex: '30',
        },
        {
          label: 'Hosts',
          flex: '120px',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
      ],
    },
    {
      id: 'canned-responses',
      url: '/gx/canned-responses/resp/',
      filterId: 'canned-responses',
      label: 'Canned responses',
      columns: [
        {
          label: 'Canned response name',
          flex: '1 1 100%',
        },
        {
          label: 'Message',
          flex: '1 1 100%',
        },
        {
          label: 'Hosts',
          flex: '120px',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
        {
          label: 'Platforms',
          flex: '80px',
        },
      ],
    },
    {
      id: 'task-rules',
      url: '/operations/task-rules/task/',
      filterId: 'task-rules',
      label: 'Task rules',
      columns: [
        {
          label: 'Rule name',
          flex: '1 1 100%',
        },
        {
          label: 'Task time',
          flex: '1 1 30%',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
        {
          label: 'Assignment',
          flex: '160px',
        },
      ],
    },
    {
      id: 'team',
      url: '/operations/team/member/',
      filterId: 'teammates',
      label: 'Team',
      columns: [
        {
          label: 'Name',
          flex: '',
        },
        {
          label: 'Company',
          flex: '',
        },
        {
          label: 'Location',
          flex: '',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
      ],
    },
    {
      id: 'notification-rules',
      url: '/operations/notifications/detail/',
      filterId: 'notifications',
      label: 'Team notification rules',
      columns: [
        {
          label: 'Rule name',
          flex: '1 1 100%',
        },
        {
          label: 'Sending time',
          flex: '1 1 100%',
        },
        {
          label: 'Hosts',
          flex: '120px',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
      ],
    },
    {
      id: 'custom-codes',
      url: '/gx/custom-codes/code/',
      filterId: 'custom-codes',
      label: 'Custom codes',
      columns: [
        {
          label: 'Custom code name',
          flex: '1 1 100%',
        },
        {
          label: 'Condition',
          flex: '1 1 100%',
        },
        {
          label: 'Hosts',
          flex: '120px',
        },
        {
          label: 'Properties',
          flex: '120px',
        },
        {
          label: 'Platforms',
          flex: '80px',
        },
      ],
    },
  ];
  activeType: any;

  tableData: any[];
  defaultFilters: any[];
  activeFilters: Filter[];
  loading: boolean;
  conditions: any;
  paginationData: PaginationMeta;
  currentOffset: any;
  searchCriteria: string;

  constructor(
    private rulesService: RuleService,
    private taskService: TasksService,
    private teamService: TeamService,
    private customCodeService: CustomCodeService,
    private propertyOnboardingService: PropertyOnboardingService
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    this.currentOffset = 0;

    if (this.searchCriteria) {
      this.searchComponent.clearValue();
    }

    this.searchCriteria = '';
    this.loading = true;
    this.activeType = this.types.find((x) => x.id === this.type);

    if (this.activeType) {
      this.setPropertyFilterRulesets();
      this.fetchData();
    }
  }

  setPropertyFilterRulesets() {
    switch (this.activeType.id) {
      case 'custom-codes':
        this.activeFilters = [
          {
            id: 'custom-codes-properties',
            label: 'Properties',
            lockable: true,
            type: 'list',
            values: 'notused',
            selectedValue: { key: this.property.id, label: this.property.name },
            inverted: this.onboardingMode,
          },
        ];
        break;

      case 'task-rules':
        this.activeFilters = [
          {
            id: 'task-rules-properties',
            label: 'Properties',
            lockable: true,
            type: 'list',
            values: 'notused',
            selectedValue: { key: this.property.id, label: this.property.name },
            inverted: this.onboardingMode,
          },
          {
            id: 'task-rules-active',
            label: 'Activated',
            lockable: false,
            type: 'list',
            values: 'notused',
            valuesData: [
              { key: 'no', label: 'No' },
              { key: 'yes', label: 'Yes' },
            ],
            selectedValue: { key: 'yes', label: 'Yes' },
          },
        ];
        break;

      case 'team':
        this.activeFilters = [
          {
            id: 'team-mates-properties',
            label: 'Properties',
            lockable: true,
            type: 'list',
            values: 'notused',
            selectedValue: { key: this.property.id, label: this.property.name },
            inverted: this.onboardingMode,
          },
        ];
        break;

      case 'canned-responses':
        this.activeFilters = [
          {
            id: 'rulesets-properties',
            label: 'Properties',
            lockable: true,
            type: 'list',
            values: 'notused',
            selectedValue: { key: this.property.id, label: this.property.name },
            inverted: this.onboardingMode,
          },
        ];
        break;

      default:
        this.activeFilters = [
          {
            id: 'rulesets-properties',
            label: 'Properties',
            lockable: true,
            type: 'list',
            values: 'notused',
            selectedValue: { key: this.property.id, label: this.property.name },
            inverted: this.onboardingMode,
          },
          {
            id: 'rulesets-active',
            label: 'Activated',
            lockable: false,
            type: 'list',
            values: 'notused',
            valuesData: [
              { key: 'no', label: 'No' },
              { key: 'yes', label: 'Yes' },
            ],
            selectedValue: { key: 'yes', label: 'Yes' },
          },
        ];
        break;
    }
  }

  fetchData() {
    if (!this.activeType) {
      return;
    }

    this.loading = true;

    switch (this.activeType.id) {
      case 'messaging-rules':
        this.fetchMessagingRules();
        break;

      case 'review-rules':
        this.fetchReviewRules();
        break;

      case 'canned-responses':
        this.fetchCannedResponses();
        break;

      case 'task-rules':
        this.fetchTaskRules();
        break;

      case 'team':
        this.fetchTeam();
        break;

      case 'notification-rules':
        this.fetchNotificationRules();
        break;

      case 'custom-codes':
        this.fetchCustomCodes();
        break;

      default:
        break;
    }
  }

  fetchMessagingRules() {
    this.rulesService
      .getRulesets('other', this.activeFilters, this.currentOffset, this.searchCriteria)
      .subscribe((res) => {
        this.tableData = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  fetchReviewRules() {
    this.rulesService
      .getRulesets('review', this.activeFilters, this.currentOffset, this.searchCriteria)
      .subscribe((res) => {
        this.tableData = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  fetchCannedResponses() {
    this.rulesService
      .getRulesets('canned', this.activeFilters, this.currentOffset, this.searchCriteria)
      .subscribe((res) => {
        this.tableData = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  fetchTaskRules() {
    this.taskService.getTasks(this.activeFilters, this.currentOffset, this.searchCriteria).subscribe((res) => {
      this.tableData = res.data;
      this.paginationData = res.meta?.pagination;
      this.loading = false;
    });
  }

  fetchTeam() {
    this.teamService
      .getTeamMembers(this.activeFilters, this.currentOffset, this.searchCriteria, 0, false)
      .subscribe((res: any) => {
        this.tableData = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  fetchNotificationRules() {
    this.rulesService
      .getRulesets('team', this.activeFilters, this.currentOffset, this.searchCriteria)
      .subscribe((res) => {
        this.tableData = res.data;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  fetchCustomCodes() {
    this.customCodeService
      .getCustomCodes(this.activeFilters, this.currentOffset, this.searchCriteria)
      .subscribe((res) => {
        this.tableData = res.data;
        this.conditions = res.meta?.conditions;
        this.paginationData = res.meta?.pagination;
        this.loading = false;
      });
  }

  addPropertyToRule(row) {
    row.adding = true;

    if (
      this.activeType.id === 'messaging-rules' ||
      this.activeType.id === 'review-rules' ||
      this.activeType.id === 'canned-responses' ||
      this.activeType.id === 'notification-rules'
    ) {
      if (row.id) {
        this.propertyOnboardingService.addPropertyToItem(row.id, this.property.id, 'rulesets').subscribe((res) => {
          row.adding = false;
          if (res) {
            this.handleAddedRow(row);
          }
        });
      }
    }

    if (this.activeType.id === 'custom-codes') {
      this.propertyOnboardingService.addPropertyToItem(row.id, this.property.id, 'custom-codes').subscribe((res) => {
        row.adding = false;
        if (res) {
          this.handleAddedRow(row);
        }
      });
    }

    if (this.activeType.id === 'task-rules' && row.uuid) {
      this.propertyOnboardingService
        .addPropertyToItem(row.uuid, this.property.id, 'operations/task-rules')
        .subscribe((res) => {
          row.adding = false;
          if (res) {
            this.handleAddedRow(row);
          }
        });
    }

    if (this.activeType.id === 'team') {
      this.propertyOnboardingService
        .addPropertyToItem(row.uuid, this.property.id, 'operations/teammates')
        .subscribe((res) => {
          row.adding = false;
          if (res) {
            this.handleAddedRow(row);
          }
        });
    }
  }

  handleAddedRow(row) {
    row.added = true;
    row.adding = false;
    row.deleted = false;
    row.deleting = false;
  }

  handleDeletedRow(row) {
    row.added = false;
    row.adding = false;
    row.deleted = true;
    row.deleting = false;
  }

  removePropertyFromRule(row) {
    row.deleting = true;

    if (
      this.activeType.id === 'messaging-rules' ||
      this.activeType.id === 'review-rules' ||
      this.activeType.id === 'canned-responses' ||
      this.activeType.id === 'notification-rules'
    ) {
      if (row.id) {
        this.propertyOnboardingService.removePropertyFromItem(row.id, this.property.id, 'rulesets').subscribe((res) => {
          row.deleting = false;
          if (res) {
            this.handleDeletedRow(row);
          }
        });
      }
    }

    if (this.activeType.id === 'custom-codes') {
      this.propertyOnboardingService
        .removePropertyFromItem(row.id, this.property.id, 'custom-codes')
        .subscribe((res) => {
          row.deleting = false;
          if (res) {
            this.handleDeletedRow(row);
          }
        });
    }

    if (this.activeType.id === 'task-rules' && row.uuid) {
      this.propertyOnboardingService
        .removePropertyFromItem(row.uuid, this.property.id, 'operations/task-rules')
        .subscribe((res) => {
          row.deleting = false;
          if (res) {
            this.handleDeletedRow(row);
          }
        });
    }

    if (this.activeType.id === 'team') {
      this.propertyOnboardingService
        .removePropertyFromItem(row.uuid, this.property.id, 'operations/teammates')
        .subscribe((res) => {
          row.deleting = false;
          if (res) {
            this.handleDeletedRow(row);
          }
        });
    }
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;
    this.fetchData();
  }

  filterReset() {
    this.setPropertyFilterRulesets();

    // strip out any lockable filters
    this.activeFilters = this.activeFilters.filter((x) => x.lockable === true);

    this.fetchData();
  }

  public handlePageChanged(newPageNo: number) {
    this.currentOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    this.fetchData();
  }

  searchUpdated(newSearch: string) {
    this.searchCriteria = newSearch;
    this.currentOffset = 0;
    this.fetchData();
  }

  enableOnboarding() {
    this.onboardingMode = true;
    this.onboardingChange.emit(true);
  }
}
